import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { assertExists } from 'common';
import { Field, FieldDataType } from 'models';

const supportedFormats = [
  // Date-only formats
  'MM/DD/YYYY', // e.g., 01/05/2023
  'M/D/YYYY', // e.g., 1/5/2023
  'DD/MM/YYYY',
  'D/M/YYYY',
  'YYYY-MM-DD',
  'YYYY-M-D',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'MM.DD.YYYY',
  'M.D.YYYY',
  'YYYY/MM/DD',
  'YYYY/M/D',
  'MMMM D, YYYY',
  'MMM D, YYYY',
  'D MMM YYYY',
  'D MMMM YYYY',

  // Date-time formats (24-hour and 12-hour)
  'MM/DD/YYYY HH:mm',
  'M/D/YYYY HH:mm',
  'MM/DD/YYYY hh:mm A',
  'M/D/YYYY hh:mm A',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-M-D HH:mm:ss',
  'MMMM D, YYYY h:mm A',
  'MMM D, YYYY h:mm A',
  'DD-MM-YYYY HH:mm',
  'D-M-YYYY HH:mm',
  'YYYY/MM/DD HH:mm:ss',
  'YYYY/M/D HH:mm:ss',
  'dddd, MMMM D YYYY h:mm A',
];

/**
 * Checks to see if the value that will be passed matches the data type of the field.
 *
 * @param field - Field.
 * @param value - Value to be passed into the field.
 * @returns - The value if it's valid or '' if not.
 */
export function parsedValidFieldValue(
  field: Field,
  value: string,
  dateAdapter?: MomentDateAdapter
): string | Number {
  if (!value) return '';
  switch (field.type) {
    case FieldDataType.integer:
      return /^\d+$/.test(value) ? value : '';
    case FieldDataType.decimal:
      return parseLocalDecimal(value);
    case FieldDataType.date:
      return parseDate(value, dateAdapter);
    default:
      return value;
  }
}

/**
 * Parse value to validate decimal value.
 * @param value - Value to be parsed.
 * @returns
 */
function parseLocalDecimal(value: string): number | string {
  const locale = navigator.language;
  const formatter = new Intl.NumberFormat(locale);
  const parts = formatter.formatToParts(1234567.89);

  const decimalSep = parts.find((p) => p.type === 'decimal')?.value || '.';
  const groupSep = parts.find((p) => p.type === 'group')?.value || ',';

  // Remove group separators, replace decimal with dot
  const normalized = value
    .replace(new RegExp(`\\${groupSep}`, 'g'), '')
    .replace(decimalSep, '.');

  const num = Number(normalized);
  return !Number.isNaN(num) ? num : '';
}

/**
 * Parse the passed string to verify it is a date or datetime.
 * @param value - Value to be parsed.
 * @param dateAdapter - Moment Date Adapter.
 * @returns
 */
function parseDate(value: string, dateAdapter?: MomentDateAdapter): string {
  assertExists(dateAdapter);
  const locale = navigator.language;
  dateAdapter.setLocale(locale);
  const valueAsMoment = dateAdapter.parse(value, supportedFormats);
  // If the value is not null and is a valid moment everything is fine.
  if (valueAsMoment && dateAdapter.isValid(valueAsMoment)) {
    // Return it in the format SQL expects.
    return valueAsMoment.format('YYYY-MM-DD HH:mm:ss');
  }
  return '';
}
